import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { sortingFilters } from '@/constants/states';
import findResult, { isChecked } from '@/utils/resultsHelper';
import { configurationCodes } from '@/configurations/constants';
import { states } from '@/constants/lifecycle';
import { blankTaskTemplate } from '../templates/state/state';
import { requestReferences } from '../../state/state';
import { allowedRequestTaskTabIndexes, claimState } from '../state/state';

const useTasksActions = () => {
  const permissions = useSelector((state) => state.auth.role.permissions);
  const { configurations } = useSelector((state) => state.configurations);
  const configuration = findResult(configurations, 'all', configurationCodes.WORKFLOW_TASK_LIST) || {
    value: { value: false },
  };
  return {
    allowRefresh: permissions.tasks.read,
    allowApprove: permissions.requests.review,
    taskLinkEnabled: isChecked(configuration.value.value),
  };
};

const basicTasksPanelState = ({ results: tasks, readErrors, readStatus }) => ({ tasks, readErrors, readStatus });

const tasksPanelPaging = ({ currentPage, totalPages: pageCount, pageSize }) => ({
  currentPage, pageCount, pageSize,
});

const tasksPanelState = (tasks, category) => {
  const item = tasks[category];
  return {
    item,
    state: {
      ...basicTasksPanelState(item),
      ...tasksPanelPaging(item),
    },
  };
};

const makeSelectTasksPanelContainer = (category) => createSelector(
  (state) => state.auth.role.permissions.tasks.read,
  (state) => state.requests.tasks[category],
  (canRead, item) => ({
    canRead,
    ...basicTasksPanelState(item),
    pageSize: tasksPanelPaging(item).pageSize,
    total: item.count,
  }),
);

const useTasksPanelContainer = ({ category }) => {
  const selector = useMemo(() => makeSelectTasksPanelContainer(category), [category]);
  return useSelector(selector);
};

const useTasksFilters = ({ category }) => {
  const { readStatus, filter, view } = useSelector((state) => state.requests.tasks[category]);
  const { role: { permissions: { taskqueries: { read: allowQueries } } } } = useSelector((state) => state.auth);

  return {
    allowQueries, filterTerm: filter, readStatus, view,
  };
};

const useTasksTableColumns = ({ category }) => {
  const { orderBy, singleSorting } = useSelector((state) => state.requests.tasks[category]) || sortingFilters();
  return { orderBy, singleSorting };
};

const useTaskViewsDropdown = ({ category }) => {
  const { readStatus, results } = useSelector((state) => state.requests.tasks.views[category]);
  return { readStatus, views: results };
};

const useTaskTools = ({ task }) => {
  const { role } = useSelector((state) => state.auth);
  return {
    ...claimState(task, role),
    canManage: role.permissions.tasks.manage,
  };
};

const makeSelectTasksPanel = (category, displayAs) => createSelector(
  (state) => state.auth,
  (state) => state.requests.tasks,
  (auth, tasks) => {
    const { item, state: panelState } = tasksPanelState(tasks, category);
    return {
      ...panelState,
      allowDatasetLink: auth.role.permissions.datasets.read,
      allowRequestLink: auth.role.permissions.requests.read,
      canEdit: auth.role.permissions.tasks.update,
      display: displayAs || item.display,
      email: auth.currentUser.email,
      filterTerm: item.filter,
      total: item.count,
    };
  },
);

const useTasksPanel = ({ category, displayAs }) => {
  const selector = useMemo(() => makeSelectTasksPanel(category, displayAs), [category, displayAs]);
  return useSelector(selector);
};

const useTaskDialogs = () => {
  const { role: { permissions: { tasks: { read, manage } } } } = useSelector((state) => state.auth);
  const { activeTask, manageTask } = useSelector((state) => state.requests.tasks);
  return {
    allowSave: activeTask.open && read,
    allowManage: manageTask.open && manage,
  };
};

const useTaskRequestDialog = () => {
  const {
    name, readStatus, saveErrors, review_approved: approved,
  } = useSelector((state) => state.requests.tasks.activeTask);
  return {
    approved, name, readStatus, saveErrors,
  };
};

const useTaskRequestDialogContent = () => {
  const {
    readErrors, readStatus, workflow: { name: workflow }, assignee, groups,
  } = useSelector((state) => state.requests.tasks.activeTask);
  return {
    assignee, groups, readErrors, readStatus, workflow,
  };
};

const useManageTaskDialog = () => {
  const {
    name, groups, saveErrors, saveStatus, assignee, isDirty,
  } = useSelector((state) => state.requests.tasks.manageTask);
  return {
    email: assignee?.email, groups, name, saveErrors, saveStatus, isDirty,
  };
};

const useTaskRequestFormActions = () => {
  const role = useSelector((state) => state.auth.role);
  const { activeTask, templates } = useSelector((state) => state.requests.tasks);
  const {
    assignee, claimStatus, completeStatus, code, fields, isDirty, name,
    permissions, readStatus, saveErrors, saveStatus, formKey,
  } = activeTask;
  const { canClaim, canEdit } = claimState({ assignee, permissions }, role);
  const { sections } = findResult(templates, 'open', formKey) || blankTaskTemplate();
  return {
    canClaim: canClaim && readStatus === states.SUCCESS,
    canEdit: canEdit && readStatus === states.SUCCESS,
    assignee,
    claimStatus,
    code,
    completeStatus,
    fields,
    isDirty,
    name,
    permissions,
    saveErrors,
    saveStatus,
    sections,
  };
};

const useTaskTabPanes = () => {
  const requests = useSelector((state) => state.requests.requests);
  const tasks = useSelector((state) => state.requests.tasks);
  const { workflows } = useSelector((state) => state.workflows);
  const { results } = requests.tasks;
  const { activeTask, templates } = tasks;
  const {
    activeStepIndex, formKey, saveErrors, permissions, fields,
  } = activeTask;
  const [request] = results;
  const { workflow, workflowKey } = requestReferences(request, workflows);
  const template = findResult(templates, 'open', formKey) || blankTaskTemplate();
  const indexes = allowedRequestTaskTabIndexes({ permissions, formKey, fields }, template);
  return {
    activeStepIndex,
    indexes,
    readStatus: workflow.readStatus,
    request,
    saveErrors,
    sections: { ...workflow.sections, ...template.sections },
    workflowKey,
  };
};

export default {
  useManageTaskDialog,
  useTaskDialogs,
  useTaskTabPanes,
  useTaskRequestDialog,
  useTaskRequestDialogContent,
  useTaskRequestFormActions,
  useTasksActions,
  useTasksFilters,
  useTasksPanel,
  useTasksPanelContainer,
  useTasksTableColumns,
  useTaskTools,
  useTaskViewsDropdown,
};
